<script lang="ts">
import {
  DEFAULT_LOCALE,
  isIndexedLocale,
  LOCALE_INFO,
  type Locale,
  localePath,
} from "$lib/i18n/locales";
import { alternatesFor } from "$lib/i18n/registry";
import { OG_CARD, OG_CARD_ALT, OG_CARD_HEIGHT, OG_CARD_WIDTH } from "./og-card";
import { absoluteUrl } from "./site";

// The head every public page shares: the title, the description, the canonical
// address, the social card and — where a page has one — its structured data.
//
// It exists because these tags are only correct together. A canonical that
// disagrees with `og:url` tells a crawler and a share preview two different
// addresses for one page; a card image with no `twitter:card` renders as a
// bare link on one network and a picture on another; a description written
// once and forgotten in the other two places is the usual way a page ends up
// sharing as its own URL. One component, one set of values, per page.
//
// What it does NOT do is invent anything. Every page passes its own title and
// description, because a shared default would be the same sentence on every
// page — which is the defect the build check over the emitted pages fails on.
//
// A page that exists in more than one language also lists every version of
// itself, this one included, plus `x-default` for a reader in none of those
// languages ($lib/i18n/registry). The list is the same on every version, and
// each version's canonical is its own address, never the English one: a
// translation that pointed its canonical at the original would be telling a
// crawler not to index it.
//
// A page in a language kept out of the index ($lib/i18n/locales,
// INDEXED_LOCALES) says `noindex, follow` whatever the page itself asked for,
// and lists no alternates: it is not offered to searchers in any language, so
// naming the indexed versions from it would be a link they do not return.

interface Props {
  /** The document title, used as-is. */
  title: string;
  /** One sentence, distinct from every other page's. */
  description: string;
  /**
   * The page's English, site-relative address, e.g. "/pricing" — also on a
   * translated page, whose own address is built from it and `locale`.
   */
  path: string;
  /** The language this version of the page is in. */
  locale?: Locale;
  /** The Open Graph object type. "website" for everything that is not an article. */
  type?: string;
  /** A ready-made JSON-LD document, or null when the page publishes none. */
  jsonLd?: string | null;
  /**
   * True for a page that stays reachable but is kept out of the search index.
   * It is `noindex, follow`: the page is dropped, the links on it still count.
   */
  noindex?: boolean;
}

let {
  title,
  description,
  path,
  locale = DEFAULT_LOCALE,
  type = "website",
  jsonLd = null,
  noindex = false,
}: Props = $props();

const indexedLanguage = $derived(isIndexedLocale(locale));
const robotsNoindex = $derived(noindex || !indexedLanguage);
const canonical = $derived(absoluteUrl(localePath(locale, path)));
const alternates = $derived(
  indexedLanguage
    ? alternatesFor(path).map((link) => ({
        hreflang: link.hreflang,
        href: absoluteUrl(link.href),
      }))
    : [],
);
const image = $derived(absoluteUrl(OG_CARD));
</script>

<svelte:head>
  <title>{title}</title>
  <meta name="description" content={description} />
  <link rel="canonical" href={canonical} />
  {#if robotsNoindex}
    <meta name="robots" content="noindex, follow" />
  {/if}
  {#each alternates as link (link.hreflang)}
    <link rel="alternate" hreflang={link.hreflang} href={link.href} />
  {/each}

  <meta property="og:type" content={type} />
  <meta property="og:site_name" content="doc.cheap" />
  <meta property="og:locale" content={LOCALE_INFO[locale].ogLocale} />
  <meta property="og:title" content={title} />
  <meta property="og:description" content={description} />
  <meta property="og:url" content={canonical} />
  <meta property="og:image" content={image} />
  <meta property="og:image:width" content={String(OG_CARD_WIDTH)} />
  <meta property="og:image:height" content={String(OG_CARD_HEIGHT)} />
  <meta property="og:image:alt" content={OG_CARD_ALT} />

  <!-- The large card rather than the small one: the image carries the price and
       the request, which are unreadable at the summary size. -->
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={title} />
  <meta name="twitter:description" content={description} />
  <meta name="twitter:image" content={image} />
  <meta name="twitter:image:alt" content={OG_CARD_ALT} />

  {#if jsonLd}
    {@html `<script type="application/ld+json">${jsonLd}<\/script>`}
  {/if}
</svelte:head>
